// Port of goalhorn-standalone-app/src/goalhorns/domain/hotkeys.py (canonical form + reserved check).

import {
  KEY_ALIASES,
  MAX_F_KEY,
  MAX_HOTKEY_KEYS,
  MODIFIER_ALIASES,
  MODIFIER_ORDER,
  RESERVED_COMBOS,
  RESERVED_KEYS,
} from "./constants";

export class HotkeyError extends Error {
  constructor(
    readonly code: "invalid" | "reserved",
    message: string,
  ) {
    super(message);
    this.name = "HotkeyError";
  }
}

const SINGLE_CHAR_RE = /^[A-Z0-9]$/;
const F_KEY_RE = /^F([0-9]{1,2})$/;
const MODIFIERS: ReadonlySet<string> = new Set(MODIFIER_ORDER);

function canonicalToken(raw: string): string {
  const upper = raw.trim().toUpperCase();
  const modifier = MODIFIER_ALIASES.get(upper);
  if (modifier !== undefined) return modifier;
  const key = KEY_ALIASES.get(upper);
  if (key !== undefined) return key;
  if (SINGLE_CHAR_RE.test(upper)) return upper;
  const fKey = F_KEY_RE.exec(upper);
  if (fKey && Number(fKey[1]) >= 1 && Number(fKey[1]) <= MAX_F_KEY) return `F${Number(fKey[1])}`;
  throw new HotkeyError("invalid", `unknown key: ${JSON.stringify(raw)}`);
}

// Sort groups for non-modifier keys: digits, letters, F-keys, Space, other.
const GROUP_DIGIT = 0;
const GROUP_LETTER = 1;
const GROUP_F = 2;
const GROUP_SPACE = 3;
const GROUP_OTHER = 4;

function sortKey(token: string): [number, number, string] {
  if (/^[0-9]+$/.test(token)) return [GROUP_DIGIT, Number(token), ""];
  if (/^F[0-9]+$/.test(token)) return [GROUP_F, Number(token.slice(1)), ""];
  if (token === "Space") return [GROUP_SPACE, 0, ""];
  if (token === "Escape") return [GROUP_OTHER, 0, ""];
  return [GROUP_LETTER, 0, token];
}

function compareKeys(a: string, b: string): number {
  const [ga, na, sa] = sortKey(a);
  const [gb, nb, sb] = sortKey(b);
  return ga - gb || na - nb || (sa < sb ? -1 : sa > sb ? 1 : 0);
}

/** Canonical hotkey string from raw tokens, e.g. ["shift","a","ctrl"] -> "Ctrl+Shift+A". */
export function formatHotkey(tokens: readonly string[]): string {
  const canon = tokens.map(canonicalToken);
  if (new Set(canon).size !== canon.length) {
    throw new HotkeyError("invalid", "duplicate key in combo");
  }
  if (canon.length === 0 || canon.length > MAX_HOTKEY_KEYS) {
    throw new HotkeyError("invalid", `a hotkey needs 1 to ${MAX_HOTKEY_KEYS} keys`);
  }
  const modifiers = MODIFIER_ORDER.filter((mod) => canon.includes(mod));
  const keys = canon.filter((token) => !MODIFIERS.has(token)).sort(compareKeys);
  if (keys.length === 0) throw new HotkeyError("invalid", "a hotkey needs a non-modifier key");
  return [...modifiers, ...keys].join("+");
}

/** Canonical hotkey, null for empty/blank input; throws HotkeyError("invalid") otherwise. */
export function normalizeHotkey(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined || raw.trim() === "") return null;
  return formatHotkey(raw.split("+"));
}

function tokenSetKey(tokens: readonly string[]): string {
  return [...new Set(tokens)].sort().join("+");
}

const RESERVED_KEYSETS: ReadonlySet<string> = new Set(
  RESERVED_COMBOS.map((combo) => tokenSetKey(combo)),
);

/** True when a canonical hotkey is reserved by the desktop app (Escape, F11, Ctrl+S, ...). */
export function isReservedHotkey(canonical: string): boolean {
  const tokens = canonical.split("+");
  if (tokens.some((token) => RESERVED_KEYS.has(token))) return true;
  return RESERVED_KEYSETS.has(tokenSetKey(tokens));
}

/** Key identity used for duplicate detection: two hotkeys clash when their key sets match. */
export function hotkeyIdentity(canonical: string): string {
  return tokenSetKey(canonical.split("+"));
}
